import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AudioElement,
    ControllableElement,
    MultiPageImageModel,
    MultiPageVideoModel,
} from '@bynder-studio/render-core';
import { List, Thumbnail } from '@bynder/design-system';
import { IconShow, IconVolumeUp } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import generateTestId from '~/helpers/testIdHelpers';
import useVisibilityControlsShortcuts from 'packages/hooks/shortcuts/useVisibilityControlsShortcuts';
import MaybeTooltip from 'packages/common/MaybeTooltip';

type Props = {
    selectedElements: ControllableElement[];
    creativeModel: MultiPageVideoModel | MultiPageImageModel;
    disabled?: boolean;
};

const AllowToggleVisibility = ({ selectedElements, creativeModel, disabled = false }: Props) => {
    const { translate } = useTranslate();
    const unlockedElements = selectedElements.filter((element) => !element.locked);
    const isAllVisible = selectedElements.every((element) => element.allowToggleVisibility);
    const isAllLocked = unlockedElements.length === 0;
    const [visibility, setVisibility] = useState(isAllVisible);
    const testId = useMemo(() => generateTestId('shots_properties_visibility_control'), []);

    const isAudioElements =
        selectedElements.length && selectedElements.every((element) => element instanceof AudioElement);

    const onToggle = useCallback(() => {
        const newVisibility = !visibility;

        unlockedElements.forEach((element) => {
            creativeModel.updateElement(element.id, { allowToggleVisibility: newVisibility });
        });

        setVisibility(newVisibility);
    }, [creativeModel, unlockedElements, visibility]);

    useEffect(() => {
        setVisibility(isAllVisible);
    }, [isAllVisible]);

    useVisibilityControlsShortcuts({ toggleVisibility: onToggle });

    return (
        <MaybeTooltip
            content={translate('editor.element.multiselect.override.tooltip')}
            hasTooltip={selectedElements.length > 1 && (!isAllLocked || disabled)}
        >
            <List.Item
                checkedVariant="switch"
                onClick={onToggle}
                isChecked={visibility}
                isDisabled={isAllLocked || disabled}
                thumbnail={<Thumbnail icon={isAudioElements ? <IconVolumeUp /> : <IconShow />} />}
                {...testId}
            >
                {isAudioElements
                    ? translate('editor.sidebar.shots.audio_control.label')
                    : translate('editor.sidebar.shots.visibility_control.label')}
            </List.Item>
        </MaybeTooltip>
    );
};

export default AllowToggleVisibility;
